import { useForm } from 'react-hook-form';
import {
    Button,
    FormInputText,
    Grid,
    Typography,
    useAppDispatch,
    useAppSelector,
    useTheme,
    Form,
    EnvTypes,
    FormCheckbox,
    Icon,
} from '../../base';
import { yupResolver } from '@hookform/resolvers/yup';
import { Directions, LoginFormFields } from '../../common';
import { loginActions } from '../../actions';
import { LoginPayload, loginSelectors } from '../../actions';
import { useNavigate } from 'react-router-dom';
import { LineWithText, LoginButton } from './LoginPageContent.styles';
import { ILoginForm, loginSchema, emptyLoginForm } from '../../common';
import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from '@react-oauth/google';
import { RoutePaths } from '../../routes/RoutePaths';

interface Props {
    isMobile: boolean;
}

const LoginPageContent = ({ isMobile }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const isLoginLoading = useAppSelector(loginSelectors.getIsLoginLoading);
    const googleLoginErrorMessage = useAppSelector(
        loginSelectors.getGoogleLoginErrorMessage
    );

    const methods = useForm<ILoginForm>({
        resolver: yupResolver(loginSchema(t)),
        mode: 'onSubmit',
        defaultValues: emptyLoginForm,
    });

    const googleLogin = useGoogleLogin({
        onSuccess: (tokenResponse) => {
            dispatch(
                loginActions.fetchGoogleLogin({
                    token: tokenResponse.access_token,
                })
            );
            console.log('Login Success:', tokenResponse);
        },
        onError: () => {
            console.log('Google Login Error');
        },
        // Default scopes are: 'openid email profile'
        // No need to specify scope for basic login
    });

    const onLoginSubmit = () => {
        const formData = methods.getValues();
        const payload: LoginPayload = {
            email: formData[LoginFormFields.username],
            password: formData[LoginFormFields.password],
            isTools: formData[LoginFormFields.isTools] || false,
        };
        dispatch(loginActions.fetchLogin(payload));
    };

    // Define button components for clarity
    const forgotPasswordButton = (
        <Grid
            item
            // Conditional sizing
            xs={isMobile ? 12 : 7.5}
            display={'flex'}
            // Conditional alignment
            justifyContent={
                isMobile
                    ? 'center'
                    : theme.direction === Directions.rtl
                      ? 'right'
                      : 'left'
            }
            pb={isMobile ? 2 : 4} // Adjust padding for mobile order
        >
            <Button
                variant={'text'}
                onClick={() => {
                    navigate(RoutePaths.forgotPassword);
                }}
            >
                {t('loginPage.forgotPassword')}
            </Button>
        </Grid>
    );

    const loginButton = (
        <Grid
            item
            xs={isMobile ? 12 : 4.5}
            display={'flex'}
            justifyContent={
                isMobile
                    ? 'center'
                    : theme.direction === Directions.rtl
                      ? 'left'
                      : 'right'
            }
            pb={2}
        >
            <LoginButton
                variant={'contained'}
                type={'submit'}
                loadingPosition={'start'}
                startIcon={<></>}
                loading={isLoginLoading}
            >
                {t('loginPage.login')}
            </LoginButton>
        </Grid>
    );

    return (
        <Grid
            container
            sx={{
                padding: isMobile ? 2 : 4,
            }}
        >
            <Grid
                item
                xs={12}
                display={'flex'}
                pt={3}
                pb={3.25}
                sx={{ justifyContent: isMobile ? 'center' : 'flex-start' }}
            >
                <Typography variant={'h3'} color={theme.palette.primary.main}>
                    {t('loginPage.title')}
                </Typography>
            </Grid>
            <Grid item xs={12} pb={3.25}>
                <Button
                    variant="outlined"
                    onClick={() => googleLogin()}
                    startIcon={<Icon name="GoogleLogoIcon" />}
                    sx={{ width: '100%' }}
                >
                    {t('loginPage.loginWithGoogle')}
                </Button>
                {googleLoginErrorMessage && (
                    <Typography variant="body1" color="error">
                        {googleLoginErrorMessage}
                    </Typography>
                )}
            </Grid>
            <Grid item xs={12} pb={3.75}>
                <LineWithText>
                    <Typography variant={'subtitle2'}>
                        {t('loginPage.or')}
                    </Typography>
                </LineWithText>
            </Grid>
            <Grid
                item
                xs={12}
                display={'flex'}
                pb={3}
                sx={{ justifyContent: isMobile ? 'center' : 'flex-start' }}
            >
                <Typography variant={'subtitle1'}>
                    {t('loginPage.loginUsingEmailAddress')}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Form methods={methods} onSubmit={onLoginSubmit}>
                    <Grid container>
                        <Grid
                            item
                            xs={12}
                            display={'flex'}
                            justifyContent={'center'}
                            pb={2}
                        >
                            <FormInputText
                                name={LoginFormFields.username}
                                control={methods.control}
                                label={t('loginPage.emailAddress')}
                                required
                                size={'medium'}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            display={'flex'}
                            justifyContent={'center'}
                            pb={4}
                        >
                            <FormInputText
                                name={LoginFormFields.password}
                                control={methods.control}
                                label={t('loginPage.password')}
                                inputType={'password'}
                                required
                                size={'medium'}
                            />
                        </Grid>
                        {process.env.VITE_ENV_TYPE === EnvTypes.LOCAL && (
                            <Grid
                                item
                                xs={12}
                                display={'flex'}
                                justifyContent={'center'}
                                pb={4}
                            >
                                <FormCheckbox
                                    name={LoginFormFields.isTools}
                                    control={methods.control}
                                    label={t('loginPage.toolsLogin')}
                                />
                            </Grid>
                        )}

                        {isMobile ? (
                            <>
                                {loginButton}
                                {forgotPasswordButton}
                            </>
                        ) : (
                            <>
                                {forgotPasswordButton}
                                {loginButton}
                            </>
                        )}
                    </Grid>
                </Form>
            </Grid>
        </Grid>
    );
};

export default LoginPageContent;
